import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.ts';
import { callApi } from '../lib/api.ts';
import { isRecent } from '../lib/clientTokens.ts';
import { isDeployBuild } from '../lib/deployJobs.ts';
import { inFlightBuildId } from '../lib/resumeFlows.ts';
import { BuildStatus } from '../components/BuildStatus.tsx';
import { BuildsTableSection } from '../components/BuildsTableSection.tsx';
import { ClientsManager } from '../components/ClientsManager.tsx';
import { DesignersPanel } from '../components/DesignersPanel.tsx';
import { FlapText } from '../components/FlapText.tsx';
import { ActionQueue } from '../components/ActionQueue.tsx';
import { FleetView } from '../components/FleetView.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { Icon } from '../components/Icon.tsx';
import { btnSolid, btnGhost, input as inputCls, card, sectionTitle, sectionDesc } from '../lib/ui.ts';
import { formatUsd } from '../lib/money.ts';
import type { Assignment, Build, BuildTotalsResponse, Client, FleetLatestResponse, Profile } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';

export function AdminDashboard() {
  const [builds, setBuilds] = useState<Build[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [designers, setDesigners] = useState<Profile[]>([]);
  const [totals, setTotals] = useState<BuildTotalsResponse | null>(null);
  const [fleetLatest, setFleetLatest] = useState<FleetLatestResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    void (async () => {
      const [b, c, a, d] = await Promise.all([
        supabase.from('builds').select('*').order('created_at', { ascending: false }).limit(100),
        supabase.from('clients').select('*').order('name'),
        supabase.from('assignments').select('*'),
        supabase.from('profiles').select('*').eq('role', 'designer').order('email'),
      ]);
      if (b.error) setErr(friendlyError(b.error, b.error.message));
      else setBuilds(b.data as Build[]);
      if (!c.error) setClients(c.data as Client[]);
      if (!a.error) setAssignments((a.data ?? []) as Assignment[]);
      if (!d.error) setDesigners((d.data ?? []) as Profile[]);
    })();
    // #GR18: durable metering totals — SUMs over the WHOLE builds table (not the capped
    // list above). Best-effort: if the view/endpoint isn't there yet (pre-mig-0018) the
    // header + fleet fall back to the windowed derivation, so the dashboard never breaks.
    void (async () => {
      try {
        setTotals(await callApi<BuildTotalsResponse>('admin-build-totals', { method: 'POST', body: '{}' }));
      } catch {
        setTotals(null);
      }
    })();
    // #GR19: per-client latest build + status flags over the FULL builds table, so the
    // Fleet's Status/Last-deploy aren't windowed. Best-effort — pre-mig-0019 the fleet
    // falls back to the (windowed) builds list, so the dashboard never breaks.
    void (async () => {
      try {
        setFleetLatest(await callApi<FleetLatestResponse>('admin-fleet-latest', { method: 'POST', body: '{}' }));
      } catch {
        setFleetLatest(null);
      }
    })();
  }, [refresh]);

  // One-time on mount: clear stranded queued/running rows so the Fleet view doesn't
  // show a ghost "building" (#6 — the deferred cluster-#2 sweep). Best-effort; if any
  // were swept, refresh so the cleared status shows.
  useEffect(() => {
    void (async () => {
      try {
        const { swept } = await callApi<{ swept: number }>('admin-sweep-builds', { method: 'POST', body: '{}' });
        if (swept > 0) setRefresh((n) => n + 1);
      } catch {
        /* best-effort — never block the dashboard on cleanup */
      }
    })();
  }, []);

  // #GR81: a stalled reconcile cron (no heartbeat tick for minutes) is now LOUD — builds
  // close only from that cron, so a silent stall = late/hung work.
  const reconcile = fleetLatest?.reconcile;
  const clientById = new Map(clients.map((c) => [c.id, c]));
  // Owner label for a build row — coalesce(current client name, denormalized label,
  // '(retired)') so a retired client's spend stays legible (pipeline-metrics §3b).
  const clientLabelFor = (b: Build) =>
    (b.client_id ? clientById.get(b.client_id)?.name : undefined) ?? b.client_label ?? '(retired)';

  return (
    <div className="space-y-8">
      <h1 className="text-xl font-bold text-ink">Admin</h1>
      {/* Metering grand-totals removed: the build engine is dormant in the hand-build model,
          so fleet-wide spend/tokens/time/builds no longer reflect reality. The backend aggregate
          (build_totals view · totals.grand · admin-fleet endpoint) is kept, and per-client Fleet
          columns still show real figures. */}

      {reconcile?.stale && (
        <div className="rounded border border-danger bg-paper-2 p-3 text-sm text-danger">
          <Icon name="warning" className="mr-1 inline-block h-4 w-4 align-[-0.2em]" /> Builds aren't finishing automatically right now (last check{' '}
          {reconcile.ageMs != null ? `${Math.round(reconcile.ageMs / 60_000)} min ago` : 'a while ago'}
          ) — a clean redeploy restarts the background worker; until then, opening the Fleet runs a one-off catch-up.
        </div>
      )}

      {err && <p className="text-sm text-danger">{err}</p>}

      <FleetView
        clients={clients}
        builds={builds}
        assignments={assignments}
        designers={designers}
        totals={totals?.perClient}
        latest={fleetLatest?.perClient}
        onChange={() => setRefresh((n) => n + 1)}
      />

      {/* Action queue moved above config; collapsible, default OPEN (it's actionable). */}
      <CollapsibleSection id="admin.actionQueue" title="Action queue" defaultOpen>
        <ActionQueue />
      </CollapsibleSection>

      <ClientsManager clients={clients} onChange={() => setRefresh((n) => n + 1)} />

      {/* #28 — invite/onboard a designer to the cockpit (scaling-plan §3). */}
      <DesignersPanel />

      {/* Dev/QA tools tucked behind a collapsed disclosure (not day-to-day). */}
      <CollapsibleSection id="admin.developer" title="Developer">
        <Ops clients={clients} builds={builds} onChange={() => setRefresh((n) => n + 1)} />
      </CollapsibleSection>

      {/* Recent builds — build history / BI; collapsible, default CLOSED. */}
      <CollapsibleSection id="admin.recentBuilds" title="Recent builds">
      <BuildsTableSection
        title="Recent builds"
        builds={builds}
        header={
          <tr>
            <Th>When</Th>
            <Th>Client</Th>
            <Th>Job</Th>
            <Th>Model</Th>
            <Th>Tokens</Th>
            <Th>Cost</Th>
            <Th>Outcome</Th>
            <Th>Preview</Th>
            <Th>Production</Th>
          </tr>
        }
        haystack={(b) => {
          const c = b.client_id ? clientById.get(b.client_id) : undefined;
          return [c?.name, c?.slug, b.client_label, b.job_type, b.outcome, b.model, new Date(b.created_at).toLocaleString()]
            .filter(Boolean)
            .join(' ');
        }}
        renderRow={(b) => (
          <tr key={b.id} className="border-b last:border-0">
            <Td>{new Date(b.created_at).toLocaleString()}</Td>
            <Td>{clientLabelFor(b)}</Td>
            <Td>{b.job_type}</Td>
            <Td>{b.model ?? '—'}</Td>
            {/* Dense table numbers → font-serif (Bricolage Grotesque), tabular+lining so columns align. */}
            <Td num>{(b.input_tokens + b.output_tokens).toLocaleString()}</Td>
            <Td num>{formatUsd(Number(b.cost_usd))}</Td>
            <Td>
              <Outcome outcome={b.outcome} />
            </Td>
            {!isDeployBuild(b) ? (
              // #GR35: only real SITE deploys (build-chat/assembly/promote) get a CF
              // deploy-status chip. Every other job_type — seo/brand/media/provision/
              // agent_job — has no preview website, so the provision row no longer
              // shows a phantom "building" here (the Fleet's #GR32 fix, second surface).
              // seo-intake keeps its handoff-doc link; the rest show "—".
              <>
                <Td>
                  {b.job_type === 'seo-intake' ? (
                    <SeoDocCell outcome={b.outcome} slug={(b.client_id ? clientById.get(b.client_id)?.slug : undefined) ?? b.client_label ?? undefined} />
                  ) : (
                    '—'
                  )}
                </Td>
                <Td>—</Td>
              </>
            ) : (
              <>
                <Td>
                  <BuildStatus
                    buildId={b.id}
                    initialUrl={b.preview_url}
                    live={b.outcome === 'success' && !b.preview_url && isRecent(b.created_at)}
                  />
                </Td>
                <Td>
                  {b.promote_commit_sha ? (
                    <BuildStatus
                      buildId={b.id}
                      initialUrl={b.production_url}
                      live={!b.production_url && isRecent(b.promoted_at ?? b.created_at)}
                      endpoint="promote-status"
                      readyLabel={<>Live <Icon name="check" title="live" /></>}
                      openLabel={<>open live <Icon name="external" /></>}
                    />
                  ) : (
                    '—'
                  )}
                </Td>
              </>
            )}
          </tr>
        )}
      />
      </CollapsibleSection>
    </div>
  );
}

/**
 * A collapsible section whose open/closed state is remembered PER VIEWER in
 * localStorage (keyed by a stable id). A controlled <details> — the caret + label
 * summary is the disclosure toggle; the wrapped section keeps its own heading/card.
 * All storage access is try/catch-guarded so it renders correctly in private
 * windows / cleared storage / thumbnail capture (falls back to defaultOpen).
 */
function CollapsibleSection({
  id,
  title,
  defaultOpen = false,
  children,
}: {
  id: string;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const storageKey = `alchmy.admin.collapse.${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === '1';
    } catch {
      return defaultOpen;
    }
  });
  const onToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const next = e.currentTarget.open;
    setOpen(next);
    try {
      localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      /* per-viewer convenience only — fine without it */
    }
  };
  return (
    <details open={open} onToggle={onToggle}>
      <summary className="cursor-pointer list-none text-sm font-medium text-ink-2 hover:text-ink">
        <span className="mr-1 inline-block text-ink-2">{open ? '▾' : '▸'}</span>
        {title}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

function Ops({ clients, builds, onChange }: { clients: Client[]; builds: Build[]; onChange: () => void }) {
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [promotedBuildId, setPromotedBuildId] = useState<string | null>(null);
  const [diag, setDiag] = useState<unknown>(null);
  const [selfTest, setSelfTest] = useState<{
    ok: boolean;
    results: { label: string; status: number; blocked: boolean; message: string }[];
  } | null>(null);

  useEffect(() => {
    if (!clientId && clients[0]) setClientId(clients[0].id);
  }, [clients, clientId]);

  // #GR90: the in-flight promote chip for the selected client, re-derivable after a remount so a
  // reload mid-go-live still tracks to Live. optimistic click state (promotedBuildId) takes precedence.
  const effPromotedBuildId =
    promotedBuildId ?? inFlightBuildId(builds.filter((b) => b.client_id === clientId), 'promote');

  async function run(action: 'seed' | 'promote') {
    setBusy(action);
    setMsg(null);
    setWarn(null);
    setErr(null);
    try {
      if (action === 'seed') {
        const out = await callApi<{ slug: string; commitSha: string; note: string }>(
          'seed-sandbox',
          { method: 'POST' },
        );
        setMsg(`Seeded ${out.slug} (commit ${out.commitSha.slice(0, 7)}).`);
        setWarn(out.note);
      } else {
        // Async promote: the worker dispatches the Regen-lockfile workflow on
        // app/<slug>, waits for it to succeed, THEN merges → main stays green and
        // the operator can't skip the regen. Poll the job via promote-status.
        const out = await callApi<{ status: string; buildId: string | null }>(
          'promote',
          { method: 'POST', body: JSON.stringify({ clientId }) },
        );
        setMsg('Going live — this takes a minute or two. Tracking below…');
        setPromotedBuildId(out.buildId);
        onChange();
      }
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(null);
    }
  }

  async function runDiag() {
    setBusy('diag');
    setErr(null);
    setMsg(null);
    setDiag(null);
    try {
      setDiag(await callApi('diag', { method: 'POST' }));
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(null);
    }
  }

  async function runSelfTest() {
    setBusy('selftest');
    setErr(null);
    setMsg(null);
    setSelfTest(null);
    try {
      setSelfTest(
        await callApi<{
          ok: boolean;
          results: { label: string; status: number; blocked: boolean; message: string }[];
        }>('live-selftest', { method: 'POST' }),
      );
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className={`${card} space-y-5 p-5`}>
      <div>
        <h2 className={sectionTitle}>Operations</h2>
        <p className={sectionDesc}>
          Admin/ops testing controls — full capability by design. Use against the
          <code className="mx-1 rounded bg-paper-2 px-1">app-sandbox</code>
          client; never a real client site.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => run('seed')} disabled={busy !== null} className={btnGhost}>
          {busy === 'seed' ? 'Seeding…' : 'Seed sandbox'}
        </button>

        <button onClick={runDiag} disabled={busy !== null} className={btnGhost}>
          {busy === 'diag' ? 'Checking…' : 'Run diagnostics'}
        </button>

        <button
          onClick={runSelfTest}
          disabled={busy !== null}
          className={btnGhost}
          title="Creates a throwaway live client and confirms every write path returns 403"
        >
          {busy === 'selftest' ? 'Testing…' : '🔒 Live-protection self-test'}
        </button>

        <span className="text-line">|</span>

        <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls}>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.is_live ? '🔒 ' : ''}{c.name} (/{c.slug})
            </option>
          ))}
        </select>
        <button onClick={() => run('promote')} disabled={busy !== null || !clientId} className={btnSolid}>
          <FlapText>{busy === 'promote' ? 'Going live…' : 'Go live'}</FlapText>
        </button>
      </div>

      {effPromotedBuildId && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-ink-2">Production:</span>
          <BuildStatus
            buildId={effPromotedBuildId}
            initialUrl={null}
            live
            endpoint="promote-status"
            readyLabel={<>Live <Icon name="check" title="live" /></>}
            openLabel={<>open live <Icon name="external" /></>}
            onResolved={onChange}
          />
        </div>
      )}
      {diag != null && (
        <pre className="overflow-auto rounded border bg-paper-2 p-3 text-xs">
          {JSON.stringify(diag, null, 2)}
        </pre>
      )}
      {selfTest && (
        <div className="space-y-2 rounded border bg-paper-2 p-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">is_live self-test:</span>
            {selfTest.ok ? (
              <StatusChip tone="success">PASS — all write paths blocked (403)</StatusChip>
            ) : (
              <StatusChip tone="danger">FAIL — a write path was NOT blocked</StatusChip>
            )}
          </div>
          {/* Phase 5 item 7: wrap the narrow table so it can't force a horizontal page scroll. */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-ink-2">
                <tr>
                  <th className="py-1 pr-3">Write path</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">Result</th>
                </tr>
              </thead>
              <tbody>
                {selfTest.results.map((r) => (
                  <tr key={r.label} className="border-t border-line">
                    <td className="py-1 pr-3">{r.label}</td>
                    <td className="py-1 pr-3 font-mono">{r.status}</td>
                    <td className="py-1 pr-3">
                      {r.blocked ? (
                        <span className="inline-flex items-center gap-1 text-success">403 blocked <Icon name="check" title="blocked" /></span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-danger"><Icon name="close" title="not blocked" /> NOT blocked {r.message}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-ink-2">
            Ran against a throwaway live client (zz-live-selftest), deleted afterwards. Cooper untouched.
          </p>
        </div>
      )}
      {msg && <p className="text-sm text-success">{msg}</p>}
      {warn && (
        <p className="rounded border border-warning-bright bg-paper-2 p-3 text-sm text-warning">
          {warn}
        </p>
      )}
      {err && <p className="text-sm text-danger">{err}</p>}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium text-ink-2">{children}</th>;
}
function Td({ children, num }: { children: React.ReactNode; num?: boolean }) {
  // num → font-serif (Bricolage Grotesque), tabular+lining figures so numeric columns align (#GR PR-B).
  return <td className={`px-3 py-2${num ? ' font-serif nums-tabular' : ''}`}>{children}</td>;
}

/**
 * A seo-intake row has NO preview website — it produces a handoff doc in the
 * client-sites repo. Render its status as a doc reference, never a pages.dev
 * preview / "Open website" (that affordance belongs to tokens builds only).
 */
export function SeoDocCell({ outcome, slug }: { outcome: Build['outcome']; slug?: string }) {
  const path = slug ? `docs/clients/${slug}-seo.md` : undefined;
  if (outcome === 'success') {
    return (
      <span className="text-xs text-success" title={path}>
        <span className="inline-flex items-center gap-1">SEO doc <Icon name="check" title="done" /></span>{path ? <span className="ml-1 font-mono text-ink-2">{path}</span> : null}
      </span>
    );
  }
  if (outcome === 'failed' || outcome === 'stopped') {
    return <span className="text-xs text-danger">failed</span>;
  }
  return <span className="text-xs text-warning">running…</span>;
}

export function Outcome({ outcome }: { outcome: Build['outcome'] }) {
  const tone =
    outcome === 'success'
      ? 'success'
      : outcome === 'running' || outcome === 'queued'
        ? 'info'
        : outcome === 'stopped'
          ? 'warning'
          : 'danger';
  return <StatusChip tone={tone}>{outcome}</StatusChip>;
}
